import { CSSProperties, useEffect, useRef } from 'react';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { playerPotWin, toLocaleStringNumber } from 'utils/functions';

type AmountWonPropType = {
  seatId: number;
};

export const AmountWon = ({ seatId }: AmountWonPropType) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const tableType = useTableSelector((table) => table.game?.tabletype);
  const pots = useTableSelector((table) => table.game?.pots);
  const showdown = useTableSelector((table) => table.game?.showdown);
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const potWon = playerPotWin(pots, showdown, seatId);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    if (isHandHistorySnapshot) {
      gsap.set(el, { opacity: 1, scale: 1, y: 0 });
      return;
    }

    // Grows in, holds, then drifts a little further down as it fades.
    // EN-10520: from 0.9 rather than 0.3, and a 1.04 (was 1.1) overshoot — the
    // badge sits directly under the seat card, so a big pop reached into the
    // seat below it on a 390px 9-max table.
    const popScale = 1.04;
    const settleScale = 1;
    const tl = gsap.timeline();
    tl.fromTo(
      el,
      { opacity: 0, scale: 0.9, y: -4 },
      { opacity: 1, scale: popScale, y: 0, duration: 0.4, ease: 'back.out(1.4)' }
    )
      .to(el, { scale: settleScale, duration: 0.2 })
      .to(el, { duration: 2.5 })
      .to(el, { opacity: 0, y: 14, duration: 0.5, ease: 'power2.in' });

    return () => {
      tl.kill();
    };
  }, [isHandHistorySnapshot, isPortrait, potWon]);

  if (!(potWon > 0)) return null;

  return (
    <div ref={elementRef} style={isPortrait ? containerStylePortrait : containerStyleLandscape}>
      <span style={isPortrait ? amountStylePortrait : amountStyle}>
        + {tableType === 's' && '$'}
        {toLocaleStringNumber(potWon)}
      </span>
    </div>
  );
};

// Anchored to the seat CARD (top:100%) rather than the scaled avatar box, so it
// always sits just below the name/stack line regardless of the avatar scale.
const containerBase: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  zIndex: 30,
  pointerEvents: 'none'
};

const containerStylePortrait: CSSProperties = {
  ...containerBase,
  marginTop: 3
};

const containerStyleLandscape: CSSProperties = {
  ...containerBase,
  marginTop: 2
};

const amountStyle: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 800,
  color: '#FFD700',
  textShadow: '0 0 8px rgba(255, 215, 0, 0.5)',
  whiteSpace: 'nowrap',
  background: 'rgba(0, 0, 0, 0.7)',
  padding: '0.2rem 0.5rem',
  borderRadius: '6px',
  border: '1px solid rgba(255, 215, 0, 0.3)'
};

const amountStylePortrait: CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 800,
  color: '#FFD700',
  textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
  whiteSpace: 'nowrap',
  letterSpacing: '0.02em',
  background: 'rgba(0, 0, 0, 0.75)',
  padding: '0.15rem 0.45rem',
  borderRadius: '7px',
  border: '1px solid rgba(255, 215, 0, 0.35)'
};
